import { useCallback, useEffect, useState } from "react";

import { isBankDebitPaymentMethod, PaymentMethod } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";

export type UseSplitCoverFeesParams = {
  fees: number;
  prioritizeBankPayment: boolean;
  selectedPaymentMethodIsCard: boolean;
  tip: number;
  updateFreeTipAmount: (amount: number) => void;
  allowedPaymentMethods: PaymentMethod[];
};

export const useSplitCoverFees = ({
  fees,
  prioritizeBankPayment,
  selectedPaymentMethodIsCard,
  tip,
  updateFreeTipAmount,
  allowedPaymentMethods,
}: UseSplitCoverFeesParams) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();

  const [splitCoverFeesClosed, setSplitCoverFeesClosed] = useState<boolean>(false);

  const hasCardPayment =
    allowedPaymentMethods.includes(PaymentMethod.Card) ||
    allowedPaymentMethods.includes(PaymentMethod.ApplePayOrGooglePay);
  const hasBankPayment = allowedPaymentMethods.some(isBankDebitPaymentMethod);

  const hasBankAndCardPaymentAllowed = hasCardPayment && hasBankPayment;

  const shouldShowSplitCoverFeesInfobox =
    hasBankAndCardPaymentAllowed && prioritizeBankPayment && selectedPaymentMethodIsCard && !tip;

  useEffect(() => {
    // When we show the split/cover fees infobox, we reset the close state
    if (shouldShowSplitCoverFeesInfobox) {
      setSplitCoverFeesClosed(false);
      logAmplitudeEvent(AmplitudeEvents.FormShowSplitOrCoverFees);
    }
  }, [logAmplitudeEvent, shouldShowSplitCoverFeesInfobox]);

  const handleCoverFees = useCallback(() => {
    updateFreeTipAmount(fees);
    enqueueSnackbar(t("common", "paymentMethod.coverFeesMessage"), { vibe: "positive" });
    logAmplitudeEvent(AmplitudeEvents.FormClickPaymentMethodCoverFees);
    logAmplitudeEvent(AmplitudeEvents.FormClickSplitOrCoverFees, { feesAmount: "cover" });
  }, [enqueueSnackbar, fees, logAmplitudeEvent, t, updateFreeTipAmount]);

  const handleSplitFees = useCallback(() => {
    const flooredSplitFees = Math.floor(fees / 2);
    updateFreeTipAmount(flooredSplitFees);
    enqueueSnackbar(t("common", "paymentMethod.coverFeesMessage"), { vibe: "positive" });
    logAmplitudeEvent(AmplitudeEvents.FormClickPaymentMethodSplitFees);
    logAmplitudeEvent(AmplitudeEvents.FormClickSplitOrCoverFees, { feesAmount: "split" });
  }, [enqueueSnackbar, fees, logAmplitudeEvent, t, updateFreeTipAmount]);

  const onSplitCoverFeesClose = useCallback(() => {
    setSplitCoverFeesClosed(true);
    logAmplitudeEvent(AmplitudeEvents.FormClickCloseSplitOrCoverFees);
  }, [logAmplitudeEvent]);

  return {
    handleCoverFees,
    handleSplitFees,
    onSplitCoverFeesClose,
    showSplitCoverFeesInfobox: shouldShowSplitCoverFeesInfobox && !splitCoverFeesClosed,
  };
};
